import {
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem
} from '@material-ui/core'
import clsx from 'clsx'
import { Plus, Trash2 } from 'lucide-react'
import { useContext, useEffect, useState } from 'react'
import { SocketContext } from '../../context/Socket/SocketContext'
import toastError from '../../errors/toastError'
import api from '../../services/api'
import { ButtonPrimary } from '../ButtonPrimary'
import { ButtonSecondary } from '../ButtonSecondary'
import SelectInput from '../SelectInput'
import { useStyles } from './style'

const TicketAttendantsModal = ({ open, onClose, ticket }) => {
  // const { user } = useContext(AuthContext)
  const classes = useStyles()
  const socketManager = useContext(SocketContext)
  const [attendants, setAttendants] = useState([])
  const [availableUsers, setAvailableUsers] = useState([])
  const [allUsers, setAllUsers] = useState([])
  const [departments, setDepartments] = useState([])
  const [selectedDepartmentId, setSelectedDepartmentId] = useState('')
  const [selectedUserId, setSelectedUserId] = useState('')
  const [loading, setLoading] = useState(false)
  const [loadingUsers, setLoadingUsers] = useState(false)
  const [loadingDepartments, setLoadingDepartments] = useState(false)

  useEffect(() => {
    if (open && ticket) {
      loadAttendants()
      loadAvailableUsers()
      loadDepartments()
    }
  }, [open, ticket])

  const loadAttendants = async () => {
    try {
      setLoading(true)
      const { data } = await api.get(`/ticket-attendants/${ticket.id}`)
      setAttendants(data)
      setLoading(false)
    } catch (err) {
      toastError(err)
      setLoading(false)
    }
  }

  const loadAvailableUsers = async () => {
    try {
      setLoadingUsers(true)
      const { data } = await api.get('/users')
      // Filter out users that are not admins/agents or already attendants
      const filtered = data.users.filter(
        u =>
          (u.profile === 'user' || u.profile === 'admin') &&
          !attendants.find(a => a.userId === u.id) &&
          u.id !== ticket.userId
      )
      setAllUsers(filtered)
      setAvailableUsers(filtered)
      setLoadingUsers(false)
    } catch (err) {
      toastError(err)
      setLoadingUsers(false)
    }
  }

  const loadDepartments = async () => {
    try {
      setLoadingDepartments(true)
      const { data } = await api.get('/queue')
      // Filtrar departamentos que não são bots
      const filteredDepartments = data.filter(dept => !dept.isBot)
      setDepartments(filteredDepartments)
      setLoadingDepartments(false)
    } catch (err) {
      toastError(err)
      setLoadingDepartments(false)
    }
  }

  // Filter users when department is selected
  useEffect(() => {
    if (selectedDepartmentId === '') {
      // No filter, show all users
      setAvailableUsers(allUsers)
    } else {
      // Filter users by department
      const filtered = allUsers.filter(u => {
        return (
          u.queues &&
          u.queues.some(q => q.id === parseInt(selectedDepartmentId))
        )
      })
      setAvailableUsers(filtered)
      // Reset selected user if not in filtered list
      if (
        selectedUserId &&
        !filtered.find(u => u.id === parseInt(selectedUserId))
      ) {
        setSelectedUserId('')
      }
    }
  }, [selectedDepartmentId, allUsers, selectedUserId])

  const handleAddAttendant = async () => {
    if (!selectedUserId) {
      return
    }

    try {
      setLoading(true)
      await api.post('/ticket-attendants', {
        ticketId: ticket.id,
        userId: parseInt(selectedUserId)
      })

      setSelectedUserId('')
      await loadAttendants()
      await loadAvailableUsers()

      // Emit socket event
      const socket = socketManager.GetSocket()
      socket.emit('joinChatBox', `${ticket.id}`)
    } catch (err) {
      toastError(err)
    } finally {
      setLoading(false)
    }
  }

  const handleRemoveAttendant = async userId => {
    try {
      setLoading(true)
      await api.delete(`/ticket-attendants/${ticket.id}/${userId}`)
      await loadAttendants()
      await loadAvailableUsers()
    } catch (err) {
      toastError(err)
    } finally {
      setLoading(false)
    }
  }

  const isMainAttendant = userId => ticket.userId === userId

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        Gerenciar Atendentes
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <SelectInput
              label="Filtrar por Departamento"
              value={selectedDepartmentId}
              onChange={e => setSelectedDepartmentId(e.target.value)}
              disabled={loading || loadingDepartments}
              fullWidth
            >
              <MenuItem value="">Todos os Departamentos</MenuItem>
              {departments.map(dept => (
                <MenuItem key={dept.id} value={dept.id}>
                  {dept.name}
                </MenuItem>
              ))}
            </SelectInput>
          </Grid>
          <Grid item xs={12}>
            <SelectInput
              label="Adicionar Atendente"
              value={selectedUserId}
              onChange={e => setSelectedUserId(e.target.value)}
              disabled={loading || loadingUsers}
              fullWidth
            >
              {availableUsers.length === 0 && (
                <MenuItem disabled>Nenhum atendente disponível</MenuItem>
              )}
              {availableUsers.map(u => (
                <MenuItem key={u.id} value={u.id}>
                  {u.name} ({u.email})
                </MenuItem>
              ))}
            </SelectInput>
          </Grid>
          <Grid item xs={12}>
            <ButtonPrimary
              onClick={handleAddAttendant}
              fullWidth
              disabled={loading || !selectedUserId}
              startIcon={<Plus />}
              label="Adicionar"
            />
          </Grid>
          <Grid item xs={12}>
            <h3 className={classes.title}>Atendentes Atuais</h3>
            {loading && <CircularProgress />}
            {!loading && (
              <List className={classes.list}>
                {attendants.map(att => (
                  <ListItem
                    key={att.userId}
                    className={clsx(
                      classes.listItem,
                      isMainAttendant(att.userId) && classes.mainAttendant
                    )}
                    disableGutters
                  >
                    <ListItemText
                      primary={att.user.name}
                      secondary={`${att.user.email}${
                        isMainAttendant(att.userId) ? ' (Principal)' : ''
                      }`}
                    />
                    {!isMainAttendant(att.userId) && (
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={() => handleRemoveAttendant(att.userId)}
                          disabled={loading}
                          className={classes.iconButtonDelete}
                        >
                          <Trash2 size={20} />
                        </IconButton>
                      </ListItemSecondaryAction>
                    )}
                    {isMainAttendant(att.userId) && (
                      <Chip label="Principal" size="small" />
                    )}
                  </ListItem>
                ))}
              </List>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <ButtonSecondary onClick={onClose} label="Fechar" />
      </DialogActions>
    </Dialog>
  )
}

export default TicketAttendantsModal
